// client/src/components/admin/EmailSettings.js
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Switch, FormControlLabel,
  CircularProgress, Snackbar, Alert, Paper
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import AppBarHeader from '../AppBarHeader';
import axiosInstance from '../../axiosInstance';
import { jwtDecode } from 'jwt-decode';

const SMTP_KEYS = ['smtp.host', 'smtp.port', 'smtp.secure', 'smtp.user', 'smtp.pass', 'smtp.from', 'app.frontendUrl'];

function EmailSettings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;

  const [values, setValues] = useState({
    'smtp.host': '',
    'smtp.port': '587',
    'smtp.secure': false,
    'smtp.user': '',
    'smtp.pass': '',
    'smtp.from': '',
    'app.frontendUrl': '',
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!isAdmin) { navigate('/dashboard'); return; }
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, navigate]);

  async function loadSettings() {
    setLoading(true);
    try {
      const authHeader = { headers: { Authorization: `Bearer ${token}` } };
      const results = await Promise.all(
        SMTP_KEYS.map(k =>
          axiosInstance.get(`/settings/${k}`, authHeader)
            .then(r => ({ key: k, value: r.data.value }))
            .catch(() => ({ key: k, value: null }))
        )
      );
      const merged = { ...values };
      for (const { key, value } of results) {
        if (value !== null && value !== undefined) {
          merged[key] = key === 'smtp.secure' ? value === true || value === 'true' : String(value);
        }
      }
      setValues(merged);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const authHeader = { headers: { Authorization: `Bearer ${token}` } };
      await Promise.all(
        SMTP_KEYS.map(k =>
          axiosInstance.put(`/settings/${k}`, { value: values[k] }, authHeader)
        )
      );
      setSnackbar({ open: true, message: 'Email settings saved.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save settings.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleTestEmail() {
    setTesting(true);
    try {
      const res = await axiosInstance.post('/settings/test-email', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnackbar({ open: true, message: res.data.message, severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.error || 'Test email failed.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setTesting(false);
    }
  }

  const handleLogout = () => { localStorage.removeItem('token'); navigate('/login'); };

  if (loading) {
    return (
      <>
        <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      </>
    );
  }

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
      <Box sx={{ maxWidth: 680, margin: '0 auto', p: 4 }}>
        <Button variant="outlined" onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
          ← Back to Admin Dashboard
        </Button>

        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Email Settings
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>SMTP Configuration</Typography>

          <TextField
            label="Host"
            fullWidth
            value={values['smtp.host']}
            onChange={e => setValues(v => ({ ...v, 'smtp.host': e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Port"
            type="number"
            fullWidth
            value={values['smtp.port']}
            onChange={e => setValues(v => ({ ...v, 'smtp.port': e.target.value }))}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={values['smtp.secure'] === true || values['smtp.secure'] === 'true'}
                onChange={e => setValues(v => ({ ...v, 'smtp.secure': e.target.checked }))}
              />
            }
            label="TLS on connect (secure)"
            sx={{ mb: 2, display: 'block' }}
          />
          <TextField
            label="Username (leave blank for no auth)"
            fullWidth
            value={values['smtp.user']}
            onChange={e => setValues(v => ({ ...v, 'smtp.user': e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Box sx={{ mb: 2 }}>
            {showPass ? (
              <TextField
                label="Password"
                fullWidth
                value={values['smtp.pass']}
                onChange={e => setValues(v => ({ ...v, 'smtp.pass': e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <Button size="small" onClick={() => setShowPass(false)}>Hide</Button>
                  ),
                }}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Password"
                  fullWidth
                  value={values['smtp.pass'] ? '••••••••' : ''}
                  disabled
                />
                <Button variant="outlined" size="small" onClick={() => setShowPass(true)}>
                  Change
                </Button>
              </Box>
            )}
          </Box>
          <TextField
            label="From Address"
            fullWidth
            value={values['smtp.from']}
            onChange={e => setValues(v => ({ ...v, 'smtp.from': e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Frontend URL (used in email links)"
            fullWidth
            placeholder="https://your-app-domain.com"
            value={values['app.frontendUrl']}
            onChange={e => setValues(v => ({ ...v, 'app.frontendUrl': e.target.value }))}
            sx={{ mb: 3 }}
          />

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : null}
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </Button>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Test Email</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sends a test email to your admin account using the current saved settings.
            Save first, then test.
          </Typography>
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={18} /> : <EmailIcon />}
            onClick={handleTestEmail}
            disabled={testing}
          >
            {testing ? 'Sending…' : 'Send Test Email'}
          </Button>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default EmailSettings;
